import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

interface Ogrenci {
  id: string;
  ogrenci_adi: string;
  veli_adi: string | null;
  telefon: string | null;
  sinif: string | null;
  branslar: string[] | null;
  aktif: boolean | null;
  toplam_ders_sayisi: number | null;
  toplam_odeme: number | null;
}

export default function OgrencilerimPage() {
  const { ogretmenRecord } = useAuth();
  const [students, setStudents] = useState<Ogrenci[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ogretmenRecord?.id) { setLoading(false); return; }
    supabase.from('egtpanel_ogrenciler')
      .select('id, ogrenci_adi, veli_adi, telefon, sinif, branslar, aktif, toplam_ders_sayisi, toplam_odeme')
      .eq('atanan_ogretmen_id', ogretmenRecord.id)
      .order('ogrenci_adi')
      .then(({ data }) => { setStudents(data || []); setLoading(false); });
  }, [ogretmenRecord]);

  if (loading) return (
    <div className="p-6 flex items-center justify-center py-32">
      <i className="ri-loader-4-line text-3xl text-slate-300 animate-spin" />
    </div>
  );

  const aktifCount = students.filter(s => s.aktif).length;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Bana Yönlendirilen Öğrenciler</h1>
        <p className="text-slate-500 text-sm mt-1">Sizin takibinizde olan öğrenciler</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[
          { label: 'Toplam Öğrenci', val: students.length, color: 'text-slate-700', bg: 'bg-slate-50' },
          { label: 'Aktif', val: aktifCount, color: 'text-green-700', bg: 'bg-green-50' },
          { label: 'Pasif', val: students.length - aktifCount, color: 'text-amber-700', bg: 'bg-amber-50' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-xl p-4`}>
            <p className="text-xs text-slate-500 font-medium">{s.label}</p>
            <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.val}</p>
          </div>
        ))}
      </div>

      {students.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 p-16 text-center">
          <i className="ri-user-add-line text-5xl text-slate-200 block mb-3" />
          <p className="text-slate-500 font-medium">Henüz size atanmış öğrenci yok</p>
          <p className="text-slate-400 text-sm mt-1">Yönetici size öğrenci atadığında burada görünecek</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {students.map(s => (
            <div key={s.id} className="bg-white rounded-xl border border-slate-100 p-5 hover:border-slate-200 transition-all">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-purple-600 text-white font-bold">
                    {s.ogrenci_adi.charAt(0)}
                  </div>
                  <div>
                    <div className="font-semibold text-slate-800">{s.ogrenci_adi}</div>
                    <div className="text-xs text-slate-400">{s.sinif || '—'}</div>
                  </div>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${s.aktif ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                  {s.aktif ? 'Aktif' : 'Pasif'}
                </span>
              </div>
              <div className="space-y-1.5 mb-4">
                {s.veli_adi && (
                  <div className="flex items-center gap-2 text-sm">
                    <i className="ri-user-line text-slate-400 text-base w-4" />
                    <span className="text-slate-600">{s.veli_adi}</span>
                  </div>
                )}
                {s.telefon && (
                  <div className="flex items-center gap-2 text-sm">
                    <i className="ri-phone-line text-slate-400 text-base w-4" />
                    <span className="text-slate-600">{s.telefon}</span>
                  </div>
                )}
                {s.branslar && s.branslar.length > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <i className="ri-book-open-line text-slate-400 text-base w-4" />
                    <span className="text-slate-600">{s.branslar.join(', ')}</span>
                  </div>
                )}
              </div>
              <div className="flex gap-4 pt-3 border-t border-slate-50 text-center">
                <div className="flex-1">
                  <div className="text-lg font-bold text-slate-800">{s.toplam_ders_sayisi || 0}</div>
                  <div className="text-xs text-slate-400">Toplam Ders</div>
                </div>
                <div className="flex-1">
                  <div className="text-lg font-bold text-slate-800">
                    {s.toplam_odeme ? `₺${Math.round(Number(s.toplam_odeme) * 0.5).toLocaleString('tr-TR')}` : '—'}
                  </div>
                  <div className="text-xs text-slate-400">Kazancım</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}